import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import BottleMenu from "@/components/BottleMenu";
import { cartTotals, ALL_BOTTLES } from "@/lib/bottles";
import {
  CheckCircle2, Calendar, Users, Wine, Ticket, Clock, AlertTriangle, ArrowUpRight, Check, Sparkles, Trash2, X,
} from "lucide-react";

// Soho69 is open 11:00 – 04:00 every day. Guests can book any arrival hour
// from 11:00 through 03:00 (last possible arrival 1 hour before close).
const ARRIVAL_TIMES = [
  "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00",
  "19:00", "20:00", "21:00", "22:00", "23:00", "00:00", "01:00", "02:00",
  "03:00",
];
const PKG_DOT = {
  smirnoff: { color: "#FF1B8D", tierKey: "book.tier.standard" },
  ciroc: { color: "#FF6BC6", tierKey: "book.tier.premium" },
  belvedere: { color: "#FFC857", tierKey: "book.tier.exclusive" },
};

export default function Book() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const initialType = (() => {
    const tp = params.get("type");
    if (tp === "barorder" || tp === "byo" || tp === "bar") return "barorder";
    // "ticket" type is no longer supported on /book — the 6+6 loyalty card on
    // /tickets is the only ticket-style product. Fall back to table booking.
    return "table";
  })();
  const [type, setType] = useState(initialType);
  const [tables, setTables] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [bottleCart, setBottleCart] = useState({});
  // For bar pre-order: optionally also book a table on the side
  // "alsoBookingTable" is a no-op legacy toggle removed from the UI in Feb
  // 2026 (see cart-table-line above). Kept as a permanently-false constant so
  // the rest of the form's conditional rendering + submit logic still reads
  // linearly. Any /book?type=barorder guest who ALSO wants a table simply
  // switches type back to "table" from the header toggle.
  const alsoBookingTable = false;

  // Pre-select a package only if the user came in from a deep link
  // (e.g. /book?pkg=belvedere). Otherwise the basket starts empty until the
  // guest taps a package card themselves.
  const initialPkg = params.get("pkg") || "";
  const initialDate = params.get("date") || new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const initialEvent = params.get("event") || "";
  const initialNotes = params.get("notes") || "";

  const [form, setForm] = useState({
    table_package: initialPkg, name: "", email: "", whatsapp: "",
    date: initialDate, arrival_time: "23:00", party_size: 4, quantity: 2,
    event_title: initialEvent, notes: initialNotes,
  });

  useEffect(() => { api.get("/tables").then((r) => setTables(r.data)).catch(() => {}); }, []);

  // If the user arrived from /tables with notes that look like a bottle list,
  // pre-populate the bottle cart so the menu reflects their pre-picked bottles.
  useEffect(() => {
    const n = initialNotes;
    if (!n) return;
    const next = {};
    ALL_BOTTLES.forEach((b) => {
      const re = new RegExp(`(\\d+)× ${b.name.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}`);
      const m = n.match(re);
      if (m) next[b.id] = parseInt(m[1], 10);
    });
    if (Object.keys(next).length) setBottleCart(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const incBottle = (id) => setBottleCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const decBottle = (id) =>
    setBottleCart((c) => {
      const n = { ...c };
      if ((n[id] || 0) > 1) n[id] -= 1;
      else delete n[id];
      return n;
    });
  const bottleTotals = useMemo(() => cartTotals(bottleCart), [bottleCart]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedPkg = useMemo(() => tables.find((tt) => tt.id === form.table_package), [tables, form.table_package]);

  const packagesRef = useRef(null);
  const detailsRef = useRef(null);
  const summaryRef = useRef(null);

  const selectAndScroll = (id) => {
    set("table_package", id);
    setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // The backend re-derives bottle notes server-side (from the catalogue)
    // for both table and barorder flows, so the client only needs to send
    // structured line items + the guest's own free-form notes.
    const userNotes = form.notes || "";
    const extraNotes = userNotes;  // sent to /bookings/barorder

    try {
      const origin_url = window.location.origin;
      let checkoutUrl = null;
      if (type === "table") {
        if (!form.table_package) {
          toast.error(t("book.table.emptyError") || "Please pick a package first.");
          setSubmitting(false);
          return;
        }
        const bottles = Object.entries(bottleCart)
          .map(([sku, qty]) => ({ sku, qty: Number(qty) }))
          .filter((b) => b.qty > 0);
        const { data } = await api.post("/bookings/table", {
          table_package: form.table_package, name: form.name, email: form.email, whatsapp: form.whatsapp,
          date: form.date, arrival_time: form.arrival_time, party_size: Number(form.party_size),
          notes: userNotes, bottles, origin_url,
        });
        checkoutUrl = data.checkout_url;
      } else if (type === "barorder") {
        if (bottleTotals.count === 0) {
          toast.error(t("book.barorder.emptyError"));
          setSubmitting(false);
          return;
        }
        if (alsoBookingTable) {
          // Bar pre-order + table booking → use table endpoint and forward bottles
          const bottles = Object.entries(bottleCart)
            .map(([sku, qty]) => ({ sku, qty: Number(qty) }))
            .filter((b) => b.qty > 0);
          const { data } = await api.post("/bookings/table", {
            table_package: form.table_package, name: form.name, email: form.email, whatsapp: form.whatsapp,
            date: form.date, arrival_time: form.arrival_time, party_size: Number(form.party_size),
            notes: userNotes, bottles, origin_url,
          });
          checkoutUrl = data.checkout_url;
        } else {
          const { data } = await api.post("/bookings/barorder", {
            name: form.name, email: form.email, whatsapp: form.whatsapp,
            date: form.date || "", arrival_time: form.arrival_time || "",
            bottles: Object.entries(bottleCart)
              .map(([sku, qty]) => ({ sku, qty: Number(qty) }))
              .filter((b) => b.qty > 0),
            notes: extraNotes, origin_url,
          });
          checkoutUrl = data.checkout_url;
        }
      } else {
        // Legacy ticket flow — no payment yet, keep local success screen.
        const { data } = await api.post("/bookings/ticket", {
          event_title: form.event_title || "General Entry", name: form.name, email: form.email,
          whatsapp: form.whatsapp, date: form.date, quantity: Number(form.quantity), notes: userNotes,
        });
        setDone(data.booking);
        toast.success(t("book.confirm"));
        setSubmitting(false);
        return;
      }
      if (checkoutUrl) {
        toast.success("Redirecting to secure payment…");
        window.location.href = checkoutUrl;
        return;
      }
      toast.error("Could not start payment. Please try again.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-32 text-center overflow-x-hidden" data-testid="booking-success">
        <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center ring-glow"
             style={{ background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" }}>
          <CheckCircle2 className="text-white" size={40} />
        </div>
        <h1 className="h-display text-4xl md:text-6xl mt-6 break-words">
          {t("book.success.title.1")} <span className="stroke-pink">{t("book.success.title.2")}</span>
        </h1>
        <p className="text-white/70 mt-4 text-sm md:text-base">
          {t("book.success.sub")} ({done.whatsapp}) {t("book.success.within")}
        </p>
        <div className="surface rounded-4xl mt-8 p-5 md:p-6 text-left">
          <div className="text-[10px] uppercase tracking-[0.32em] text-neon">{t("book.success.ref")}</div>
          <div className="font-mono text-xs mt-1 break-all text-white/80">{done.id}</div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="text-white/50 uppercase text-[10px] tracking-widest">Type</div>
            <div className="break-words">{done.type === "table" ? done.table_name : done.event_title}</div>
            <div className="text-white/50 uppercase text-[10px] tracking-widest">{t("book.summary.date")}</div>
            <div>{done.date}</div>
            {done.type === "table" ? (
              <>
                <div className="text-white/50 uppercase text-[10px] tracking-widest">{t("book.summary.arrival")}</div>
                <div>{done.arrival_time}</div>
                <div className="text-white/50 uppercase text-[10px] tracking-widest">{t("book.summary.party")}</div>
                <div>{done.party_size} {t("book.people")}</div>
                <div className="text-white/50 uppercase text-[10px] tracking-widest">Total</div>
                <div className="text-white/70">€{done.total_price_eur ?? done.price_eur}</div>
                <div className="text-white/50 uppercase text-[10px] tracking-widest">30% Deposit Paid</div>
                <div className="text-neon font-bold">€{done.deposit_eur ?? "—"}</div>
                <div className="text-white/50 uppercase text-[10px] tracking-widest">70% Balance Due at Club</div>
                <div className="text-gold font-bold">€{done.balance_eur ?? "—"}</div>
              </>
            ) : (
              <>
                <div className="text-white/50 uppercase text-[10px] tracking-widest">{t("book.summary.tickets")}</div>
                <div>{done.quantity}</div>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 max-w-md mx-auto p-4 rounded-2xl border border-neon/40 bg-black/40 text-sm text-white/80">
          <strong className="text-white">30% Deposit Paid · </strong>
          <strong className="text-gold">70% Balance Due at Club.</strong>
          <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-neon">
            We&apos;ve emailed your full receipt to {done.email}
          </div>
        </div>

        <button onClick={() => { setDone(null); setForm({ ...form, name: "", email: "", whatsapp: "", notes: "" }); }}
                data-testid="book-another-btn" className="btn-pink mt-8">
          {t("book.success.another")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-12 pt-12 md:pt-24 pb-20 md:pb-32 overflow-x-hidden">
      {/* Shared SVG chroma-key filter — drops black bg on bottle photos */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <filter id="book-bottle-chroma" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      1 1 1 0 -0.55"
            />
            <feComponentTransfer>
              <feFuncA type="linear" slope="25" intercept="-8" />
            </feComponentTransfer>
          </filter>
        </defs>
      </svg>

      {/* ── HERO HEADER ── */}
      <div className="text-center">
        <h1 className="sr-only">{t("book.overline")} — {t("book.title.1")} {t("book.title.2")}</h1>
        <img
          src="/banners/reserve-your-table.png"
          alt="VIP Bottle Service — Reserve Your Table · Three signature packages, choose your night, we'll handle the rest."
          data-testid="reserve-your-table-heading"
          loading="eager"
          decoding="async"
          className="block w-full max-w-[1100px] mx-auto h-auto"
        />
      </div>

      <div className="mt-10 md:mt-12 grid sm:grid-cols-3 gap-3">
        <InfoPill icon="🍾" title={t("book.pill.1.t")} sub={t("book.pill.1.s")} />
        <InfoPill icon="⏰" title={t("book.pill.2.t")} sub={t("book.pill.2.s")} />
        <InfoPill icon="🔞" title={t("book.pill.3.t")} sub={t("book.pill.3.s")} />
      </div>

      <div className="mt-8 md:mt-10 inline-flex flex-wrap bg-black/30 border border-white/10 rounded-full p-1">
        <button type="button" data-testid="toggle-type-table" onClick={() => setType("table")}
                className={"px-4 md:px-5 py-2 md:py-2.5 text-[10px] md:text-xs font-bold uppercase tracking-[0.22em] md:tracking-[0.28em] rounded-full flex items-center gap-2 transition " +
                  (type === "table" ? "text-white shadow-[0_0_16px_rgba(255,27,141,0.55)]" : "text-white/60 hover:text-white")}
                style={type === "table" ? { background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" } : {}}>
          <Wine size={14} /> {t("book.toggle.table")}
        </button>
        <button type="button" data-testid="toggle-type-barorder" onClick={() => setType("barorder")}
                className={"px-4 md:px-5 py-2 md:py-2.5 text-[10px] md:text-xs font-bold uppercase tracking-[0.22em] md:tracking-[0.28em] rounded-full flex items-center gap-2 transition " +
                  (type === "barorder" ? "text-white shadow-[0_0_16px_rgba(255,27,141,0.55)]" : "text-white/60 hover:text-white")}
                style={type === "barorder" ? { background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" } : {}}>
          <Sparkles size={14} /> {t("book.toggle.barorder")}
        </button>
      </div>

      <form onSubmit={submit} className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10 w-full min-w-0" ref={detailsRef}>
        <div className="lg:col-span-2 flex flex-col gap-6 md:gap-8 min-w-0">
          <div className="surface rounded-4xl p-5 md:p-8 order-3">
            <div className="font-heading text-2xl md:text-4xl">{t("book.details.title")}</div>
            <p className="text-white/60 text-xs md:text-sm mt-2">{t("book.details.sub")}</p>

            <div className="mt-5 md:mt-6 space-y-5">
              <Field label={t("book.field.name")}>
                <Input data-testid="input-name" required value={form.name}
                       onChange={(e) => set("name", e.target.value)} placeholder={t("book.placeholder.name")} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={t("book.field.email")}>
                  <Input data-testid="input-email" required type="email" value={form.email}
                         onChange={(e) => set("email", e.target.value)} placeholder="tu@email.com" />
                  <p className="text-[10px] uppercase tracking-[0.22em] text-neon mt-1.5 font-bold">
                    📧 Required · your confirmation + proof of purchase
                  </p>
                </Field>
                <Field label={t("book.field.whatsapp")}>
                  <Input data-testid="input-whatsapp" required value={form.whatsapp}
                         onChange={(e) => set("whatsapp", e.target.value)} placeholder="+34 600 000 000" />
                </Field>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-3 md:p-4">
                <div className="w-5 h-5 rounded-md bg-emerald-500 flex items-center justify-center shrink-0">
                  <Check size={12} className="text-black" strokeWidth={3} />
                </div>
                <p className="text-xs md:text-sm text-white/85">{t("book.confirm")}</p>
              </div>

              {type === "ticket" && (
                <Field label={t("book.field.event")}>
                  <Input data-testid="input-event-title" value={form.event_title}
                         onChange={(e) => set("event_title", e.target.value)} placeholder={t("book.placeholder.event")} />
                </Field>
              )}

              {/* Table pre-order card: users on /book?type=table see the
                  selected package as a "cart line" and can remove it to fall
                  back to a plain Bar Pre-Order (barorder). */}
              {type === "table" && form.table_package && (
                <div
                  className="flex items-center justify-between gap-3 rounded-2xl border border-neon/40 bg-neon/5 p-3 md:p-4"
                  data-testid="cart-table-line"
                >
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-neon/80 font-bold">
                      In cart
                    </div>
                    <div className="text-sm md:text-base font-bold text-white truncate">
                      {selectedPkg?.name ?? form.table_package}
                    </div>
                    <div className="text-xs text-white/60 mt-0.5">
                      Table Package · €{selectedPkg?.price_eur ?? "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="cart-remove-table"
                    onClick={() => {
                      set("table_package", "");
                      setType("barorder");
                    }}
                    className="text-xs uppercase tracking-widest text-white/70 hover:text-red-400 border border-white/15 hover:border-red-400 rounded-full px-3 py-1.5 shrink-0"
                    aria-label="Remove table from cart"
                  >
                    Remove
                  </button>
                </div>
              )}

              {(type === "table") && (
                <Field label={t("book.field.package")}>
                  <Select
                    data-testid="input-package"
                    value={form.table_package}
                    onChange={(e) => set("table_package", e.target.value)}
                    required
                  >
                    {tables.map((tt) => (
                      <option key={tt.id} value={tt.id}>
                        {tt.name} — €{tt.price_eur}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                {(type === "table" || (type === "barorder" && alsoBookingTable)) && (
                  <Field label={t("book.field.party")}>
                    <Input
                      data-testid="input-party-size"
                      type="number" min={1} max={30} required
                      placeholder={t("book.placeholder.party")}
                      value={form.party_size}
                      onChange={(e) => set("party_size", e.target.value)} />
                    <p className="mt-1.5 text-[11px] text-white/55">
                      {t("book.field.party.hint")}
                    </p>
                  </Field>
                )}
                {type === "ticket" && (
                  <Field label={t("book.field.tickets")}>
                    <Input
                      data-testid="input-quantity"
                      type="number" min={1} max={20} required
                      value={form.quantity}
                      onChange={(e) => set("quantity", e.target.value)} />
                  </Field>
                )}
                <Field label={t("book.field.date")}>
                  <Input
                    data-testid="input-date"
                    required
                    type="date" value={form.date}
                    onChange={(e) => set("date", e.target.value)} />
                </Field>
              </div>

              {(type === "table" || (type === "barorder" && alsoBookingTable)) && (
                <Field label={t("book.field.arrival")}>
                  <Select data-testid="input-arrival-time" value={form.arrival_time}
                          onChange={(e) => set("arrival_time", e.target.value)} required>
                    {ARRIVAL_TIMES.map((tm) => (<option key={tm} value={tm}>{tm}</option>))}
                  </Select>
                </Field>
              )}

              <Field label={t("book.field.notes")}>
                <textarea data-testid="input-notes" rows={3} value={form.notes}
                          onChange={(e) => set("notes", e.target.value)} placeholder={t("book.placeholder.notes")}
                          className="w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon resize-none" />
              </Field>
            </div>
          </div>

          {type === "table" && (
            <div ref={packagesRef} className="surface rounded-4xl p-5 md:p-8 order-1">
              <img loading="lazy" decoding="async"                 src="https://customer-assets.emergentagent.com/job_soho69-events/artifacts/xguiatgz_FB15AFFD-AC26-41BE-9FBE-6EEA8E67465A.png"
                alt="Pick your package"
                data-testid="pick-your-package-title"
                draggable={false}
                className="block mx-auto w-full max-w-[420px] h-auto select-none"
                style={{
                  filter:
                    "url(#book-bottle-chroma) saturate(1.15) contrast(1.05) drop-shadow(0 0 18px rgba(255,27,141,0.55)) drop-shadow(0 0 50px rgba(255,27,141,0.4))",
                }}
              />
              <p className="text-white/60 text-xs md:text-sm mt-3 text-center">{t("book.bottle.sub")}</p>

              <div className="mt-5 md:mt-6 flex flex-col gap-4 md:gap-5">
                {["smirnoff", "ciroc", "belvedere"].map((id) => {
                  const tt = tables.find((x) => x.id === id);
                  if (!tt) return null;
                  const meta = PKG_DOT[id];
                  const isSelected = form.table_package === id;
                  const bottleSrc =
                    id === "smirnoff"
                      ? "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/v2xnjob5_2251D45F-704E-4AD3-8985-41807EF1B1F5.png"
                      : id === "ciroc"
                      ? "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/x7b5d1n0_B58A2135-4280-40BA-A51B-073205FAFD33.png"
                      : "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/6m3o2whn_012B99D8-A70B-4BEE-9339-CDF5B9254A7A.png";
                  return (
                    <div
                      key={id}
                      data-testid={`select-table-${id}`}
                      onClick={() => set("table_package", id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); set("table_package", id); } }}
                      className="relative rounded-3xl transition cursor-pointer"
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(255,27,141,0.06) 0%, rgba(0,0,0,0.55) 60%, rgba(255,27,141,0.06) 100%)",
                        border: `1.5px solid ${isSelected ? "rgba(255,27,141,1)" : "rgba(255,27,141,0.4)"}`,
                        boxShadow: isSelected
                          ? "0 0 22px rgba(255,27,141,0.55), inset 0 0 22px rgba(255,27,141,0.18)"
                          : "inset 0 0 14px rgba(255,27,141,0.06)",
                      }}
                    >
                      <div className="flex items-stretch min-h-[120px] md:min-h-[150px]">
                        {/* LEFT — tier + name + small price + capacity */}
                        <div className="flex-1 p-4 md:p-6 flex flex-col justify-center min-w-0">
                          <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em]" style={{ color: meta.color }}>
                            {t(meta.tierKey)}
                          </div>
                          <div className="font-heading text-xl md:text-4xl lg:text-5xl text-white mt-1 leading-[0.95] truncate">
                            {tt.name.toUpperCase()}
                          </div>
                          <div className="mt-1.5 md:mt-2 flex items-baseline gap-1">
                            <span className="font-display italic text-sm md:text-base" style={{ color: "#FF6BC6" }}>€</span>
                            <span className="font-heading text-lg md:text-xl" style={{ color: "#FF6BC6" }}>{tt.price_eur}</span>
                          </div>
                          <div className="text-white/65 text-xs mt-1">{tt.capacity}</div>
                        </div>

                        {/* CENTER — big gold price + RESERVE link (desktop) */}
                        <div className="hidden md:flex flex-col items-center justify-center px-4 lg:px-8 shrink-0 gap-2">
                          <div
                            className="font-heading leading-none tracking-tight"
                            style={{
                              fontSize: "clamp(2.6rem, 4.5vw, 4rem)",
                              fontWeight: 900,
                              color: "#FF1B8D",
                              textShadow: "0 0 4px rgba(255,27,141,0.25), 0 2px 0 rgba(0,0,0,0.45)",
                            }}
                          >
                            €{tt.price_eur}
                          </div>
                          <button
                            type="button"
                            data-testid={`reserve-table-${id}`}
                            onClick={(e) => { e.stopPropagation(); selectAndScroll(id); }}
                            className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-bold uppercase tracking-[0.36em] text-neon hover:text-white transition"
                          >
                            {t("packages.reserve")} <ArrowUpRight size={14} />
                          </button>
                        </div>

                        {/* RIGHT — bottle photo */}
                        <div className="flex shrink-0 w-20 md:w-28 lg:w-36 relative items-center justify-center self-stretch">
                          <div
                            className="absolute inset-y-0 left-0 w-px"
                            style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,27,141,0.4) 50%, transparent 100%)" }}
                          />
                          <div
                            className="absolute inset-0 m-auto pointer-events-none"
                            style={{
                              width: "120%",
                              height: "180%",
                              background:
                                "radial-gradient(ellipse at center, rgba(255,27,141,0.4) 0%, rgba(255,27,141,0.18) 40%, transparent 70%)",
                              filter: "blur(12px)",
                            }}
                          />
                          <img loading="lazy" decoding="async"                             src={bottleSrc}
                            alt={`${tt.name} bottle`}
                            data-testid={`book-bottle-${id}`}
                            draggable={false}
                            className="absolute left-1/2 -translate-x-1/2 w-auto select-none pointer-events-none"
                            style={{
                              top: id === "ciroc" ? "32px" : "50%",
                              left: "50%",
                              transform: id === "ciroc" ? "translateX(-50%)" : "translate(-50%, -50%)",
                              height: id === "belvedere" ? "120%" : id === "ciroc" ? "auto" : "108%",
                              maxHeight: id === "belvedere" ? "175px" : id === "ciroc" ? "150px" : "160px",
                              zIndex: 5,
                              filter:
                                "url(#book-bottle-chroma) drop-shadow(0 0 12px rgba(255,27,141,0.7)) drop-shadow(0 0 26px rgba(255,27,141,0.4))",
                            }}
                          />
                        </div>
                      </div>

                      {/* Mobile-only RESERVE bar at the bottom of each row */}
                      <button
                        type="button"
                        data-testid={`reserve-table-mobile-${id}`}
                        onClick={(e) => { e.stopPropagation(); selectAndScroll(id); }}
                        className="md:hidden flex w-full items-center justify-between gap-3 px-4 py-3 border-t border-neon/30 text-neon font-bold uppercase tracking-[0.32em] text-[11px]"
                      >
                        {t("packages.reserve")} <ArrowUpRight size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full VIP bottle menu — for both table bookings AND bar pre-orders.
              barorder path is fully transparent (no black card) so the neon
              PNG heading blends into the page background. table path keeps
              the surface card behind the "Add extras" heading. */}
          {(type === "table" || type === "barorder") && (
            <div
              className={
                (type === "barorder"
                  ? "pt-2 md:pt-4"
                  : "surface rounded-4xl p-5 md:p-8") + " order-2"
              }
              data-testid="book-bottle-menu"
            >
              {type === "barorder" ? (
                <img
                  src="/banners/pick-your-bottles.png"
                  alt="Pick Your Bottles — Build your own package · pick the bottles, we'll have them ready when you arrive · No table required"
                  data-testid="pick-your-bottles-heading"
                  loading="eager"
                  decoding="async"
                  className="block w-full max-w-[1100px] mx-auto h-auto"
                />
              ) : (
                <>
                  <div className="font-heading text-2xl md:text-4xl">
                    {t("book.bottles.title")}
                  </div>
                  <p className="text-white/60 text-xs md:text-sm mt-2">
                    {t("book.bottles.sub")}
                  </p>
                </>
              )}
              {type === "table" && (
                <div
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-gold text-[10px] md:text-xs uppercase tracking-[0.22em] md:tracking-[0.28em] font-bold"
                  style={{ background: "linear-gradient(135deg, rgba(255,200,87,0.12), rgba(255,27,141,0.12))" }}
                >
                  ✦ {t("bottles.perk")}
                </div>
              )}
              <div className="mt-6">
                <BottleMenu
                  cart={bottleCart}
                  onInc={incBottle}
                  onDec={decBottle}
                  showHeader={false}
                  compact
                  accordion
                />
              </div>
            </div>
          )}
        </div>

        <aside ref={summaryRef} className="lg:sticky lg:top-28 h-fit surface rounded-4xl p-5 md:p-7">
          <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{t("book.summary")}</div>
          <div className="mt-2 flex items-start justify-between gap-3">
            <div className="font-heading text-2xl md:text-4xl break-words flex-1 min-w-0">
              {type === "table"
                ? (selectedPkg?.name || t("book.pick"))
                : type === "barorder"
                ? t("book.barorder.title")
                : t("book.toggle.ticket")}
            </div>
            {type === "table" && selectedPkg && (
              <button
                type="button"
                data-testid="summary-remove-table"
                onClick={() => {
                  set("table_package", "");
                  setType("barorder");
                }}
                aria-label="Remove table from checkout"
                title="Remove table"
                className="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/80 hover:border-red-400 hover:text-red-400 hover:bg-red-500/10 text-[10px] uppercase tracking-[0.24em] font-bold transition"
              >
                <X size={14} strokeWidth={2.5} /> Remove
              </button>
            )}
          </div>

          <div className="mt-5 md:mt-6 space-y-3 text-sm border-t border-white/10 pt-5 md:pt-6">
            <Row icon={Calendar} label={t("book.summary.date")}>{form.date || "—"}</Row>
            {type === "table" && (
              <>
                <Row icon={Clock} label={t("book.summary.arrival")}>{form.arrival_time}</Row>
                <Row icon={Users} label={t("book.summary.party")}>{form.party_size} {t("book.people")}</Row>
                <Row label={t("book.summary.capacity")}>{selectedPkg?.capacity || "—"}</Row>
              </>
            )}
            {type === "barorder" && alsoBookingTable && (
              <>
                <Row icon={Clock} label={t("book.summary.arrival")}>{form.arrival_time}</Row>
                <Row icon={Users} label={t("book.summary.party")}>{form.party_size} {t("book.people")}</Row>
              </>
            )}
            {type === "ticket" && (
              <>
                <Row label={t("book.summary.event")}>{form.event_title || t("book.toggle.ticket")}</Row>
                <Row icon={Ticket} label={t("book.summary.tickets")}>{form.quantity}</Row>
              </>
            )}
          </div>

          {type === "table" && selectedPkg && (
            <div className="mt-5 md:mt-6 border-t border-white/10 pt-5 md:pt-6 space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="text-white/60 text-xs uppercase tracking-widest">{t("book.summary.total")}</span>
                <span className="font-heading text-2xl text-white/80">€{selectedPkg.price_eur + bottleTotals.sum}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-neon text-xs uppercase tracking-widest font-bold">30% Deposit today</span>
                <span data-testid="deposit-due" className="font-heading text-4xl md:text-5xl text-neon">€{Math.round((selectedPkg.price_eur + bottleTotals.sum) * 0.3)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-white/50 text-[10px] uppercase tracking-widest">70% Balance at venue</span>
                <span data-testid="balance-due" className="text-white/70 font-heading text-lg">€{(selectedPkg.price_eur + bottleTotals.sum) - Math.round((selectedPkg.price_eur + bottleTotals.sum) * 0.3)}</span>
              </div>
              <p className="text-[9px] uppercase tracking-[0.28em] text-white/40 pt-1">
                30% Deposit Paid · 70% Balance Due at Club.
              </p>
            </div>
          )}

          {type === "barorder" && bottleTotals.count > 0 && (
            <div className="mt-5 md:mt-6 border-t border-white/10 pt-5 md:pt-6 space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="text-white/60 text-xs uppercase tracking-widest">{t("book.summary.total")}</span>
                <span className="font-heading text-2xl text-white/80">€{bottleTotals.sum}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-neon text-xs uppercase tracking-widest font-bold">30% Deposit today</span>
                <span data-testid="deposit-due-bar" className="font-heading text-4xl text-neon">€{Math.round(bottleTotals.sum * 0.3)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-white/50 text-[10px] uppercase tracking-widest">70% Balance at venue</span>
                <span className="text-white/70 font-heading text-lg">€{bottleTotals.sum - Math.round(bottleTotals.sum * 0.3)}</span>
              </div>
              <p className="text-[9px] uppercase tracking-[0.28em] text-white/40 pt-1">
                30% Deposit Paid · 70% Balance Due at Club.
              </p>
            </div>
          )}

          {/* Bottle menu add-ons summary — for table and bar pre-order */}
          {(type === "table" || type === "barorder") && bottleTotals.count > 0 && (
            <div data-testid="bottle-summary" className="mt-4 border-t border-white/10 pt-4">
              <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold flex items-center justify-between">
                <span>{bottleTotals.count} {t("bottles.selected")}</span>
                <span className="text-gold font-heading text-xl">+ €{bottleTotals.sum}</span>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-white/70">
                {bottleTotals.items.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2 group">
                    <span className="truncate">{b.qty}× {b.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-white/50 tabular-nums">€{(b.price || 0) * b.qty}</span>
                      <button
                        type="button"
                        data-testid={`remove-bottle-${b.id}`}
                        aria-label={`Remove ${b.name}`}
                        title={`Remove ${b.name}`}
                        onClick={() => setBottleCart((c) => { const n = { ...c }; delete n[b.id]; return n; })}
                        className="text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-md p-1 transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button type="submit" data-testid="submit-booking"
                  disabled={submitting || (type === "table" && !form.table_package)}
                  className="btn-pink mt-5 md:mt-6 w-full">
            {submitting ? t("book.submitting") : t("book.submit")}
          </button>

          <div className="mt-4 flex items-start gap-2 text-[10px] text-white/40 uppercase tracking-widest">
            <AlertTriangle size={12} className="mt-0.5 text-neon shrink-0" />
            {t("book.disclaimer")}
          </div>
        </aside>

        {/* ─────────── Sticky mobile basket banner ───────────
            Always visible on iPhones so guests can jump to the summary +
            submit no matter how far they've scrolled the bottle list. */}
        {(() => {
          // For the TABLE flow the bottles are extras on top of a package —
          // until a package is picked the basket is effectively empty (you
          // can't checkout bottles without a table). For VIP Pre-Order
          // (barorder) the bottles ARE the order.
          const tableValid = type === "table" && !!selectedPkg;
          const barValid = type === "barorder" && bottleTotals.count > 0;
          if (!tableValid && !barValid) return null;

          const grandTotal = tableValid
            ? selectedPkg.price_eur + bottleTotals.sum
            : bottleTotals.sum;
          const itemCount = tableValid
            ? 1 + bottleTotals.count
            : bottleTotals.count;
          // Tables & bar are 30% deposit online; loyalty is 100% (handled elsewhere).
          const deposit = Math.round(grandTotal * 0.3);
          return (
            <div
              data-testid="mobile-basket-banner"
              className="lg:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-3
                         pointer-events-none"
            >
              <button
                type="button"
                onClick={() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                data-testid="mobile-basket-cta"
                className="pointer-events-auto w-full flex items-center justify-between gap-3
                           px-5 py-3.5 rounded-full text-white font-bold uppercase tracking-[0.18em]
                           text-[11px] shadow-[0_8px_28px_rgba(255,27,141,0.55)]
                           border border-white/20 backdrop-blur-sm"
                style={{
                  background:
                    "linear-gradient(135deg,#FF1B8D 0%,#FF6BC6 50%,#FF1B8D 100%)",
                }}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/35 text-[11px]">
                    {itemCount}
                  </span>
                  <span className="truncate">View basket</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] opacity-80">Deposit</span>
                  <span className="text-base">€{deposit}</span>
                  <ArrowUpRight size={16} />
                </span>
              </button>
            </div>
          );
        })()}
      </form>
    </div>
  );
}

function InfoPill({ icon, title, sub }) {
  return (
    <div className="glass rounded-2xl px-4 py-3 md:px-5 md:py-4 flex items-start gap-3">
      <div className="text-2xl shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="font-bold leading-tight text-sm md:text-base">{title}</div>
        <div className="text-white/60 text-xs md:text-sm mt-1 leading-snug">{sub}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
function Input(props) {
  return (
    <input {...props}
      className="w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon text-white placeholder:text-white/30" />
  );
}
function Select(props) {
  return (
    <select {...props}
      className="w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon text-white">
      {props.children}
    </select>
  );
}
function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-white/50 flex items-center gap-2 uppercase text-[10px] tracking-widest shrink-0">
        {Icon && <Icon size={12} className="text-neon" />} {label}
      </span>
      <span className="text-white text-right break-words min-w-0">{children}</span>
    </div>
  );
}
